import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionIcon, Affix, Indicator, Stack, Tooltip } from '@mantine/core';
import { useClickOutside, useMediaQuery } from '@mantine/hooks';
import {
  IconBolt,
  IconBrandWhatsapp,
  IconChevronUp,
  IconMessageChatbot,
  IconPhoneIncoming,
} from '@tabler/icons-react';
import { useServiceHubCounts } from './useServiceHubCounts';
import classes from './ServiceHubFAB.module.css';

/** Synchron mit `components/layout/BottomNav.module.css` → `.bottomNav { height: … }` */
const BOTTOM_NAV_HEIGHT_PX = 70;

function fmt(n: number): string {
  return n > 9 ? '9+' : String(n);
}

export interface ServiceHubFABProps {
  /** Ungelesene KIAN-Antworten. */
  kianUnread: number;
  /** Läuft gerade ein KIAN-Stream (visuelles „aktiv“). */
  isStreaming: boolean;
  isOnline: boolean;
  /** Öffnet das KIAN-Panel. */
  onKianOpen: () => void;
}

/**
 * ServiceHub — Speed-Dial unten rechts, ersetzt den nackten KIAN-FAB.
 *
 * Eingeklappt: ein Pfeil-hoch-Knopf mit Blitz und Neon-Glow, dazu drei
 * Zähler-Badges am Rand (oben rechts KIAN, Mitte rechts Chat, unten rechts
 * Anrufe). Ausgeklappt: KIAN-, WhatsApp- und Anruf-Knopf poppen gestaffelt
 * auf, der Pfeil dreht nach unten und klappt wieder ein.
 */
export function ServiceHubFAB({ kianUnread, isStreaming, isOnline, onKianOpen }: ServiceHubFABProps) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const hubRef = useClickOutside<HTMLDivElement>(() => setExpanded(false));
  const { chatUnread, chatUnanswered, callsOpen } = useServiceHubCounts();
  // Ungelesenes geht vor; erst wenn alles gelesen ist, zeigt das Chat-Badge gelb,
  // wie viele Kunden noch auf eine Antwort warten.
  const chatBadge = chatUnread > 0 ? chatUnread : chatUnanswered;
  const chatBadgeUnanswered = chatUnread === 0 && chatUnanswered > 0;

  // Wie `BottomNav` in `AppLayout` (`hiddenFrom="sm"`) — unter sm ist die Leiste sichtbar; FAB sitzt sonst oben drauf
  const bottomNavVisible = useMediaQuery('(max-width: 47.99em)');
  // `--fab-lift` setzen Seiten mit einer Eingabeleiste am unteren Rand (Chat-Inbox)
  // auf deren Höhe. Der Knopf weicht dann nach oben aus, statt dem Schreibfeld
  // rechts Platz wegzunehmen — das machte es auf dem Handy unbrauchbar schmal.
  const bottom = bottomNavVisible
    ? `calc(1.5rem + ${BOTTOM_NAV_HEIGHT_PX}px + env(safe-area-inset-bottom, 0px) + var(--fab-lift, 0px))`
    : 'calc(1.5rem + env(safe-area-inset-bottom, 0px) + var(--fab-lift, 0px))';

  const openKian = () => {
    setExpanded(false);
    onKianOpen();
  };

  const goTo = (path: string) => {
    setExpanded(false);
    navigate(path);
  };

  return (
    // `--fab-right`/`--fab-size` (global.css) halten Knopf und den Freiraum, den
    // Seiten unten rechts lassen (`--fab-clearance`), zwangsläufig zusammen.
    <Affix position={{ bottom, right: 'var(--fab-right, 1.5rem)' }} zIndex={5000}>
      <div ref={hubRef} className={classes.hub}>
        {expanded && (
          <Stack gap={12} align="center">
            <Tooltip
              label={isOnline ? 'KIAN fragen · ⌘K / Strg+K' : 'Offline – Nachrichten werden gepuffert'}
              position="left"
            >
              <Indicator
                inline
                disabled={kianUnread === 0}
                label={fmt(kianUnread)}
                size={18}
                color="red"
                zIndex={1}
                processing={isStreaming && kianUnread === 0}
                className={classes.dialItem}
                style={{ animationDelay: '0.1s' }}
              >
                <ActionIcon
                  size={48}
                  radius="xl"
                  variant="filled"
                  color={isOnline ? 'cyberOrange' : 'gray'}
                  className={classes.dialButton}
                  onClick={openKian}
                  aria-label="KIAN öffnen"
                  data-testid="servicehub-kian"
                >
                  <IconMessageChatbot size={26} />
                </ActionIcon>
              </Indicator>
            </Tooltip>

            <Tooltip
              label={
                chatBadgeUnanswered
                  ? `Chat-Inbox — ${chatUnanswered} ${chatUnanswered === 1 ? 'Chat wartet' : 'Chats warten'} auf Antwort`
                  : 'Chat-Inbox (WhatsApp / Chatwerk)'
              }
              position="left"
            >
              <Indicator
                inline
                disabled={chatBadge === 0}
                label={fmt(chatBadge)}
                size={18}
                color={chatBadgeUnanswered ? 'yellow' : 'red'}
                autoContrast
                zIndex={1}
                className={classes.dialItem}
                style={{ animationDelay: '0.05s' }}
              >
                <ActionIcon
                  size={48}
                  radius="xl"
                  variant="filled"
                  className={`${classes.dialButton} ${classes.whatsapp}`}
                  onClick={() => goTo('/chats')}
                  aria-label="Chat-Inbox öffnen"
                  data-testid="servicehub-chat"
                >
                  <IconBrandWhatsapp size={26} />
                </ActionIcon>
              </Indicator>
            </Tooltip>

            <Tooltip label="Verpasste Anrufe & Rückrufe" position="left">
              <Indicator
                inline
                disabled={callsOpen === 0}
                label={fmt(callsOpen)}
                size={18}
                color="red"
                zIndex={1}
                className={classes.dialItem}
              >
                <ActionIcon
                  size={48}
                  radius="xl"
                  variant="filled"
                  color="blue"
                  className={classes.dialButton}
                  onClick={() => goTo('/admin/callers')}
                  aria-label="Verpasste Anrufe öffnen"
                  data-testid="servicehub-calls"
                >
                  <IconPhoneIncoming size={26} />
                </ActionIcon>
              </Indicator>
            </Tooltip>
          </Stack>
        )}

        <Tooltip label={expanded ? 'ServiceHub einklappen' : 'ServiceHub öffnen'} position="left">
          <button
            type="button"
            className={classes.mainFab}
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? 'ServiceHub einklappen' : 'ServiceHub öffnen'}
            aria-expanded={expanded}
            data-testid="servicehub-fab"
          >
            <IconBolt size={13} className={classes.bolt} />
            <IconChevronUp
              size={26}
              stroke={2.5}
              className={expanded ? `${classes.arrow} ${classes.arrowOpen}` : classes.arrow}
            />
            {!expanded && kianUnread > 0 && (
              <span className={`${classes.badge} ${classes.badgeKian}`} data-testid="servicehub-badge-kian">
                {fmt(kianUnread)}
              </span>
            )}
            {!expanded && chatBadge > 0 && (
              <span
                className={`${classes.badge} ${chatBadgeUnanswered ? classes.badgeChatUnanswered : classes.badgeChat}`}
                data-testid={chatBadgeUnanswered ? 'servicehub-badge-chat-unanswered' : 'servicehub-badge-chat'}
                title={chatBadgeUnanswered ? 'Chats warten auf Antwort' : undefined}
              >
                {fmt(chatBadge)}
              </span>
            )}
            {!expanded && callsOpen > 0 && (
              <span className={`${classes.badge} ${classes.badgeCalls}`} data-testid="servicehub-badge-calls">
                {fmt(callsOpen)}
              </span>
            )}
          </button>
        </Tooltip>
      </div>
    </Affix>
  );
}
